/**
 * Public Pack Builder. Compose a pack, see live progress, savings and price,
 * then add the composition to the existing cart as a single line (Phase 4).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import { SearchX } from "lucide-react";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { SafeImage } from "@/components/common/SafeImage";
import { fallbackCollectionCover } from "@/lib/catalog/fallback-images";
import { PackBottleCard } from "@/components/packs/PackBottleCard";
import { DEFAULT_PACK_FILTERS, PackFilters, type PackFilterState } from "@/components/packs/PackFilters";
import { PackSummary } from "@/components/packs/PackSummary";
import { PackVariantSelector } from "@/components/packs/PackVariantSelector";
import { PackAddons } from "@/components/packs/PackAddons";
import { PackFixedContents } from "@/components/packs/PackFixedContents";
import { PackGiftForm } from "@/components/packs/PackGiftForm";
import { packProductsQuery, publicPackQuery } from "@/lib/packs/queries";
import { resolveComposedPricing } from "@/lib/packs/pricing";
import { defaultAddonSelection, fixedItemsForVariant, validateComposition } from "@/lib/packs/composition";
import { pricePackSelectionFn } from "@/lib/packs/packs.functions";
import { buildPackCartLine } from "@/lib/packs/cart";
import { addToCart, getCart, removeLine } from "@/lib/cart/store";
import { EMPTY_GIFT_DETAILS, type PackAddonSelection, type PackGiftDetails, type PackProductCard, type PackSelection } from "@/lib/packs/types";
import { canonicalUrl } from "@/lib/seo/site";

const PAGE_SIZE = 24;

export const Route = createFileRoute("/packs/$slug")({
  validateSearch: (search: Record<string, unknown>): { edit?: string } => ({
    edit: typeof search.edit === "string" && search.edit ? search.edit : undefined,
  }),
  loader: async ({ context, params }) => {
    const data = await context.queryClient.ensureQueryData(publicPackQuery(params.slug));
    if (!data.pack) throw notFound();
  },
  head: ({ loaderData, params }) => {
    void loaderData;
    const slug = params?.slug ?? "";
    const title = `Build Your Pack — CB16 Maison`;
    const description =
      "Compose your CB16 fragrance pack bottle by bottle. Live pricing, live progress and a full summary before you commit.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "product" },
        { name: "twitter:card", content: "summary_large_image" },
        ...(slug ? [{ property: "og:url", content: canonicalUrl(`/packs/${slug}`) }] : []),
      ],
      links: slug ? [{ rel: "canonical", href: canonicalUrl(`/packs/${slug}`) }] : [],
    };
  },
  component: PackBuilderPage,
  notFoundComponent: () => (
    <Fallback title="Pack not found" body="This pack is no longer available or has not been published yet." />
  ),
  errorComponent: () => <Fallback title="Pack unavailable" body="We could not load this pack. Please try again shortly." />,
});

function PackBuilderPage() {
  const { slug } = Route.useParams();
  const { edit } = Route.useSearch();
  const navigate = useNavigate();
  const { data } = useSuspenseQuery(publicPackQuery(slug));
  const pack = data.pack;

  const [variantId, setVariantId] = useState<string | null>(null);
  const [filters, setFilters] = useState<PackFilterState>(DEFAULT_PACK_FILTERS);
  const [debounced, setDebounced] = useState<PackFilterState>(DEFAULT_PACK_FILTERS);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<PackProductCard[]>([]);
  const [selection, setSelection] = useState<PackSelection>({});
  const [addonSelection, setAddonSelection] = useState<PackAddonSelection>({});
  const [gift, setGift] = useState<PackGiftDetails>(EMPTY_GIFT_DETAILS);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [added, setAdded] = useState(false);
  const pricePack = useServerFn(pricePackSelectionFn);
  /** Keeps every card ever seen so the summary can price off-page selections. */
  const catalogRef = useRef(new Map<string, PackProductCard>());
  const restoredRef = useRef(false);

  useEffect(() => {
    if (pack && !variantId) {
      const first = pack.variants.find((v) => v.isActive) ?? pack.variants[0];
      if (first) setVariantId(first.id);
      if (pack.defaultSort) setFiltersSort(pack.defaultSort);
    }
    function setFiltersSort(sort: PackFilterState["sort"]) {
      setFilters((f) => ({ ...f, sort }));
      setDebounced((f) => ({ ...f, sort }));
    }
  }, [pack, variantId]);

  // Debounce text/price inputs; selects apply immediately via the same effect.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(filters), 250);
    return () => clearTimeout(t);
  }, [filters]);

  /**
   * "Edit pack" from the cart: restore the exact composition, then drop the
   * old line so the customer never ends up with a duplicate pack.
   */
  useEffect(() => {
    // Wait for the first page of cards so the summary can price the restored
    // bottles immediately (the catalogue cache is filled by the query effect).
    if (!edit || restoredRef.current || items.length === 0) return;
    restoredRef.current = true;
    const line = getCart().find((l) => l.pack?.packSlug === slug && l.variantId === edit);
    if (line?.pack) {
      const restored: PackSelection = {};
      for (const b of line.pack.bottles) {
        restored[b.productId] = b.qty;
        // Seed the pricing cache so bottles that live on a later page (or that
        // the current filters hide) still price correctly right after restore.
        if (!catalogRef.current.has(b.productId)) {
          catalogRef.current.set(b.productId, {
            productId: b.productId,
            slug: b.slug,
            name: b.name,
            shortDescription: null,
            image: null,
            family: null,
            gender: null,
            concentration: null,
            badge: null,
            unitPrice: b.unitPrice,
            bottleSizeMl: line.pack.bottleSizeMl,
            onHand: b.qty,
            lowThreshold: 0,
            stockStatus: "in_stock",
            isPinned: false,
            isExclusive: false,
            createdAt: null,
          });
        }
      }
      setSelection(restored);
      setVariantId(line.pack.variantId);
      removeLine(line.productId, line.variantId);
    }
    void navigate({ to: "/packs/$slug", params: { slug }, search: {}, replace: true });
  }, [edit, slug, navigate, items.length]);

  useEffect(() => {
    setPage(1);
    setItems([]);
  }, [debounced]);

  const productsQuery = useQuery({
    ...packProductsQuery({
      packSlug: slug,
      sort: debounced.sort,
      page,
      pageSize: PAGE_SIZE,
      search: debounced.search,
      family: debounced.family || undefined,
      availability: debounced.availability,
      minPrice: debounced.minPrice ? Number(debounced.minPrice) : undefined,
      maxPrice: debounced.maxPrice ? Number(debounced.maxPrice) : undefined,
    }),
    enabled: Boolean(pack),
  });

  useEffect(() => {
    const result = productsQuery.data;
    if (!result) return;
    for (const card of result.items) catalogRef.current.set(card.productId, card);
    setItems((prev) => {
      if (result.page === 1) return result.items;
      const seen = new Set(prev.map((i) => i.productId));
      return [...prev, ...result.items.filter((i) => !seen.has(i.productId))];
    });
  }, [productsQuery.data]);

  const variant = useMemo(
    () => pack?.variants.find((v) => v.id === variantId) ?? null,
    [pack, variantId],
  );

  const packAddons = useMemo(() => pack?.addons ?? [], [pack]);
  const variantFixedItems = useMemo(
    () => (pack ? fixedItemsForVariant(pack.fixedItems, variantId) : []),
    [pack, variantId],
  );
  const isGiftPack = Boolean(pack && pack.compositionMode !== "customer_choice");
  const showsBuilder = !pack || pack.compositionMode !== "fixed";

  // Required add-ons are pre-selected once the pack loads.
  useEffect(() => {
    if (packAddons.length === 0) return;
    setAddonSelection((prev) => (Object.keys(prev).length > 0 ? prev : defaultAddonSelection(packAddons)));
  }, [packAddons]);

  const validation = useMemo(
    () =>
      pack && variant
        ? validateComposition(pack, variant, selection, catalogRef.current, {
            fixedItems: pack.fixedItems,
            addons: pack.addons,
            addonSelection,
            giftMessage: gift.message,
          })
        : null,
    // `items` participates so restored selections re-price once the catalogue loads.
    [pack, variant, selection, items, addonSelection, gift.message],
  );

  const pricing = useMemo(
    () =>
      pack && variant
        ? resolveComposedPricing(pack, variant, selection, catalogRef.current, {
            addons: pack.addons,
            addonSelection,
            gift: isGiftPack ? gift : null,
          })
        : null,
    [pack, variant, selection, items, addonSelection, gift, isGiftPack],
  );

  if (!pack) return <Fallback title="Pack not found" body="This pack is no longer available." />;

  const selected = validation?.selectedBottles ?? 0;
  const required = validation?.requiredBottles ?? pack.minBottles;
  const remaining = Math.max(0, required - selected);

  const changeQty = useCallback((productId: string, qty: number) => {
    setSelection((prev) => {
      const next = { ...prev };
      if (qty <= 0) delete next[productId];
      else next[productId] = qty;
      return next;
    });
    setAdded(false);
    setAddError(null);
  }, []);

  const handleAddToCart = async () => {
    if (!pack || !variant || adding) return;
    setAdding(true);
    setAddError(null);
    try {
      // Authoritative server-side re-validation and re-pricing before the
      // composition is allowed into the cart.
      const res = await pricePack({
        data: {
          packSlug: pack.slug,
          variantId: variant.id,
          selection,
          addons: addonSelection,
          ...(isGiftPack ? { gift } : {}),
        },
      });
      if (res.error || !res.pricing || !res.validation) {
        setAddError(res.error ?? "This pack could not be verified. Please try again.");
        return;
      }
      if (!res.validation.valid) {
        setAddError(res.validation.issues[0]?.message ?? "Please adjust your pack before adding it.");
        return;
      }
      addToCart(buildPackCartLine(pack, variant, res.pricing, { fixedItems: res.fixedItems }));
      setAdded(true);
      setSelection({});
      if (isGiftPack) setGift(EMPTY_GIFT_DETAILS);
    } catch {
      setAddError("We could not add this pack right now. Please try again.");
    } finally {
      setAdding(false);
    }
  };

  const total = productsQuery.data?.total ?? items.length;
  const filtersActive =
    Boolean(debounced.search || debounced.family || debounced.minPrice || debounced.maxPrice) ||
    debounced.availability !== "all";
  const hasMore = productsQuery.data?.hasMore ?? false;
  const loadError = productsQuery.data?.error ?? null;

  return (
    <div className="bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <section className="relative overflow-hidden border-b border-black/10 bg-[#faf7f2]">
        <div className="absolute inset-0">
          <SafeImage
            src={pack.bannerImage ?? pack.heroImage}
            fallback={fallbackCollectionCover(pack.slug)}
            alt=""
            className="h-full w-full object-cover opacity-25"
          />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-20">
          <nav className="text-[11px] uppercase tracking-[0.2em] text-black/45">
            <Link to="/packs" className="hover:text-black">Packs</Link>
            <span className="mx-2">/</span>
            <span className="text-black/70">{pack.name}</span>
          </nav>
          <h1
            className="mt-5 text-4xl font-normal leading-tight text-black sm:text-5xl"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {pack.name}
          </h1>
          <div className="mt-5 h-px w-10 bg-[#C6A15B]" />
          {(pack.shortDescription || pack.description) && (
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-black/65 sm:text-base">
              {pack.shortDescription ?? pack.description}
            </p>
          )}
          {pack.marketingText && (
            <p className="mt-4 max-w-2xl text-sm italic leading-relaxed text-black/50">{pack.marketingText}</p>
          )}
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-6 py-10 lg:px-10 lg:py-14">
        <PackVariantSelector variants={pack.variants.filter((v) => v.isActive)} selectedId={variantId} onSelect={setVariantId} />

        <div className="mt-10 grid gap-10 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div>
            <div className="flex flex-col gap-6">
              <PackFixedContents items={variantFixedItems} />
              {isGiftPack && (
                <PackGiftForm options={pack.giftOptions} value={gift} onChange={setGift} />
              )}
              <PackAddons
                addons={packAddons}
                selection={addonSelection}
                onChange={(id, qty) =>
                  setAddonSelection((prev) => {
                    const next = { ...prev };
                    if (qty <= 0) delete next[id];
                    else next[id] = qty;
                    return next;
                  })
                }
              />
            </div>

            {showsBuilder && (
            <div className={variantFixedItems.length > 0 || packAddons.length > 0 ? "mt-10" : ""}>
            {pack.compositionMode === "hybrid" && (
              <h2 className="mb-5 text-lg text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
                Choose your {required} fragrance{required === 1 ? "" : "s"}
              </h2>
            )}
            <PackFilters
              value={filters}
              resultCount={total}
              onChange={setFilters}
              onReset={() => setFilters({ ...DEFAULT_PACK_FILTERS, sort: filters.sort })}
            />

            {loadError && (
              <p className="mt-6 border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">{loadError}</p>
            )}

            {productsQuery.isPending && items.length === 0 ? (
              <div className="mt-8 grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-80 animate-pulse bg-black/5" />
                ))}
              </div>
            ) : items.length === 0 ? (
              <div className="mt-10 flex flex-col items-center border border-black/10 bg-[#faf7f2] px-6 py-16 text-center">
                <SearchX className="mb-4 h-9 w-9 text-black/25" strokeWidth={1.25} aria-hidden="true" />
                <h2 className="text-xl text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {filtersActive ? "No fragrances match your filters" : "No eligible fragrances yet"}
                </h2>
                <p className="mx-auto mt-3 max-w-sm text-sm text-black/60">
                  {filtersActive
                    ? "Try a different family, widen your price range, or clear the search."
                    : "This pack has no fragrances available right now. Please check back shortly."}
                </p>
                {filtersActive && (
                  <button
                  type="button"
                  onClick={() => setFilters({ ...DEFAULT_PACK_FILTERS, sort: filters.sort })}
                  className="mt-6 border border-black px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.24em] text-black transition-colors hover:bg-black hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C6A15B]"
                >
                  Clear filters
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="mt-8 grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
                  {items.map((card) => (
                    <PackBottleCard
                      key={card.productId}
                      card={card}
                      qty={selection[card.productId] ?? 0}
                      remaining={remaining}
                      onChange={changeQty}
                    />
                  ))}
                </div>
                {hasMore && (
                  <div className="mt-10 text-center">
                    <button
                      type="button"
                      disabled={productsQuery.isFetching}
                      onClick={() => setPage((p) => p + 1)}
                      className="border border-black px-8 py-3 text-[11px] font-semibold uppercase tracking-[0.24em] text-black transition-colors hover:bg-black hover:text-white disabled:opacity-40"
                    >
                      {productsQuery.isFetching ? "Loading…" : "Load more fragrances"}
                    </button>
                  </div>
                )}
              </>
            )}
            </div>
            )}
          </div>

          <aside className="hidden lg:block">
            <div className="sticky top-28">
              <PackSummary
                pack={pack}
                variant={variant}
                validation={validation}
                pricing={pricing}
                estimatedDelivery="3 – 6 business days"
                onRemove={(id) => changeQty(id, 0)}
                onClear={() => setSelection({})}
                onAddToCart={handleAddToCart}
                adding={adding}
                addError={addError}
                added={added}
                fixedItems={variantFixedItems}
              />
            </div>
          </aside>

          <div className="lg:hidden">
            <PackSummary
              pack={pack}
              variant={variant}
              validation={validation}
              pricing={pricing}
              estimatedDelivery="3 – 6 business days"
              onRemove={(id) => changeQty(id, 0)}
              onClear={() => setSelection({})}
              onAddToCart={handleAddToCart}
              adding={adding}
              addError={addError}
              added={added}
              fixedItems={variantFixedItems}
            />
          </div>
        </div>
      </div>

      {/* Mobile sticky progress bar */}
      <div className="sticky bottom-0 z-30 border-t border-black/10 bg-white/95 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur sm:px-6 lg:hidden">
        <div className="flex items-center justify-between text-sm">
          <span className="font-semibold text-black">
            {selected} / {required} bottles
          </span>
          <span className={remaining === 0 && selected > 0 ? "font-semibold text-emerald-600" : "text-black/55"}>
            {remaining === 0 && selected > 0 ? "✓ Pack complete" : `${remaining} to go`}
          </span>
        </div>
        <div
          className="mt-2 h-1 w-full overflow-hidden rounded-full bg-black/8"
          role="progressbar"
          aria-label="Pack progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={required > 0 ? Math.min(100, Math.round((selected / required) * 100)) : 0}
        >
          <div
            className={
              "h-full rounded-full transition-all duration-500 ease-out " +
              (remaining === 0 && selected > 0 ? "bg-emerald-500" : "bg-[#C6A15B]")
            }
            style={{ width: `${required > 0 ? Math.min(100, (selected / required) * 100) : 0}%` }}
          />
        </div>
        <button
          type="button"
          disabled={remaining !== 0 || selected === 0 || !validation?.valid || adding}
          onClick={() => void handleAddToCart()}
          className="mt-3 w-full bg-black px-6 py-3.5 text-[11px] font-semibold uppercase tracking-[0.24em] text-white transition-colors hover:bg-[#C6A15B] hover:text-black disabled:cursor-not-allowed disabled:bg-black/15 disabled:text-black/45"
        >
          {adding
            ? "Adding…"
            : added
              ? "Added to bag"
              : remaining === 0 && selected > 0
                ? "Add pack to bag"
                : `Select ${remaining} more bottle${remaining === 1 ? "" : "s"}`}
        </button>
      </div>
    </div>
  );
}

function Fallback({ title, body }: { title: string; body: string }) {
  return (
    <div className="mx-auto max-w-3xl px-6 py-24 text-center" style={{ fontFamily: "'Inter', sans-serif" }}>
      <h1 className="text-3xl text-black" style={{ fontFamily: "'Playfair Display', serif" }}>{title}</h1>
      <p className="mt-4 text-sm text-black/60">{body}</p>
      <Link to="/packs" className="mt-8 inline-block text-[11px] font-semibold uppercase tracking-[0.24em] text-[#C6A15B]">
        Back to packs
      </Link>
    </div>
  );
}